import { makeAutoObservable, runInAction } from 'mobx'

import { notificationService } from '@/Core'
import { serverMessage } from '@/Ship/Helpers/serverError'

import { COLLABORA, isFromEditor, originOf, parseCollaboraMessage, postToEditor } from './collaboraMessages'
import { closeWelcomeDialog, injectHiddenElementsStyle } from './hiddenElements'
import { OpenEditSessionTask } from './OpenEditSessionTask'

import type { EditSession, OfficeFileKind, OfficeViewerMode } from './OpenEditSessionTask'

/** Тексти шухляди (`SPEC-012` §9.5; «звережено» легасі виправлено, N-15). */
export const OFFICE_VIEWER_TEXTS = {
    view: 'Переглянути',
    edit: 'Редагувати',
    save: 'Зберегти',
    successTitle: 'Успіх',
    saved: 'Файл успішно збережено.',
    errorTitle: 'Помилка',
    saveFailed: 'Помилка збереження файлу.',
    openFailed: 'Не вдалося відкрити документ у редакторі.',
} as const

type Notify = Pick<typeof notificationService, 'showSuccess' | 'showError'>

/**
 * Стан шухляди Collabora (`SPEC-012` §7.10): відкриття через `edit_session`,
 * повідомлення редактора, «Зберегти», закриття.
 *
 * Сам `iframe` і форму малює `OfficeViewer`; стор знає лише його вікно
 * (`frame`), щоб приймати повідомлення тільки від нього.
 *
 * ⚠️ Стор один на застосунок ({@see officeViewer}), а шухляда змонтована в
 * макеті (`DefaultLayoutTemplate`), а не в картці документа: ядрова форма
 * картки перемонтовує вміст, і локальний стан разом із `iframe` редактора
 * зникав одразу після `edit_session` (e2e С7.4, 2026-10-13).
 */
export class OfficeViewerStore {
    isOpen = false

    loading = false

    /** Документ завантажився в редакторі (`Document_Loaded`) */
    loaded = false

    saving = false

    error: string | null = null

    session: EditSession | null = null

    mode: OfficeViewerMode = 'view'

    kind: OfficeFileKind = 'word'

    /** Назва до відповіді `edit_session` — щоб заголовок не блимав */
    title = ''

    private frame: HTMLIFrameElement | null = null

    private request = 0

    /** Що зробити після закриття — дає той, хто відкрив (картка документа) */
    private onClosed: (() => void) | undefined = undefined

    constructor(private readonly notify: Notify = notificationService) {
        makeAutoObservable<OfficeViewerStore, 'frame' | 'request' | 'onClosed' | 'notify'>(this, {
            frame: false,
            request: false,
            onClosed: false,
            notify: false,
        })
    }

    /** Origin редактора — з `actionUrl` (у варіанті B = origin застосунку). */
    get editorOrigin(): string | null {
        return this.session ? originOf(this.session.actionUrl) : null
    }

    /** «Зберегти» — лише в режимі редагування Word (N-20). */
    get canSave(): boolean {
        return this.mode === 'edit' && this.session?.canWrite === true && this.loaded
    }

    async open(
        sessionUrl: string,
        kind: OfficeFileKind,
        mode: OfficeViewerMode,
        title: string,
        onClosed?: () => void
    ): Promise<void> {
        const request = ++this.request

        this.onClosed = onClosed
        this.isOpen = true
        this.loading = true
        this.loaded = false
        this.saving = false
        this.error = null
        this.session = null
        this.kind = kind
        this.mode = kind === 'word' ? mode : 'view'
        this.title = title

        try {
            const session = await OpenEditSessionTask(sessionUrl, kind, this.mode)

            runInAction(() => {
                if (request !== this.request) return

                this.session = session
                this.title = session.fileName || title
                this.loading = false
            })
        } catch (error: unknown) {
            runInAction(() => {
                if (request !== this.request) return

                this.loading = false
                this.error = serverMessage(error, OFFICE_VIEWER_TEXTS.openFailed)
            })
        }
    }

    attachFrame(frame: HTMLIFrameElement | null): void {
        this.frame = frame
    }

    /** Обробник `message` вікна; чуже — ігнорується (N-14). */
    handleMessage(event: Pick<MessageEvent, 'source' | 'origin' | 'data'>): void {
        if (!isFromEditor(event, this.frame?.contentWindow, this.editorOrigin)) return

        const message = parseCollaboraMessage(event.data)

        if (!message) return

        if (message.MessageId === COLLABORA.loadingStatus && message.Values?.['Status'] === COLLABORA.documentLoaded) {
            postToEditor(this.frame?.contentWindow, this.editorOrigin, { MessageId: COLLABORA.hostReady })
            injectHiddenElementsStyle(this.frame)
            closeWelcomeDialog(this.frame)
            this.loaded = true

            return
        }

        if (message.MessageId === COLLABORA.saveResponse) {
            this.saving = false

            if (message.Values?.['success'] === true) {
                this.notify.showSuccess(OFFICE_VIEWER_TEXTS.successTitle, OFFICE_VIEWER_TEXTS.saved)
            } else {
                this.notify.showError(OFFICE_VIEWER_TEXTS.errorTitle, OFFICE_VIEWER_TEXTS.saveFailed)
            }
        }
    }

    save(): void {
        if (!this.canSave) return

        this.saving = postToEditor(this.frame?.contentWindow, this.editorOrigin, {
            MessageId: COLLABORA.save,
            Values: { Notify: true },
        })
    }

    /** `Action_Close`, далі — картку документа перечитати (новий PDF, P-37). */
    close(): void {
        if (!this.isOpen) return

        postToEditor(this.frame?.contentWindow, this.editorOrigin, { MessageId: COLLABORA.close, Values: null })

        const wasOpened = this.session !== null
        const onClosed = this.onClosed

        ++this.request
        this.isOpen = false
        this.loading = false
        this.loaded = false
        this.saving = false
        this.session = null
        this.error = null
        this.frame = null
        this.onClosed = undefined

        if (wasOpened) onClosed?.()
    }
}

/** Єдина шухляда редактора документів застосунку (змонтована в макеті). */
export const officeViewer = new OfficeViewerStore()
